import { useEffect, useState } from 'react'
import { API_BASE } from '../lib/api'

export default function AdminReferralPanel({ adminToken }) {
  const [payouts, setPayouts] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('PENDING')
  const [editing, setEditing] = useState(null) // { id, amount, note, status }
  const [saving, setSaving] = useState(false)
  const [paying, setPaying] = useState(null) // id currently being paid via Razorpay
  const [msg, setMsg] = useState('')

  async function load() {
    setLoading(true)
    setMsg('')
    try {
      const r = await fetch(`${API_BASE}/affiliate/admin/payouts?status=${filter}&limit=100`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      })
      const d = await r.json()
      if (!r.ok) throw new Error(d.message)
      setPayouts(d.payouts || [])
      setTotal(d.total || 0)
    } catch (err) {
      setMsg(err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [filter])

  async function initiateRazorpayPayout(id) {
    if (!window.confirm(`Initiate Razorpay payout for record #${id}? This will immediately transfer funds.`)) return
    setPaying(id)
    setMsg('')
    try {
      const r = await fetch(`${API_BASE}/affiliate/admin/payouts/${id}/pay`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
      })
      const d = await r.json()
      if (!r.ok) throw new Error(d.message)
      setMsg(`✓ Payout #${id} sent via Razorpay. Razorpay Payout ID: ${d.razorpayPayoutId} (${d.razorpayStatus})`)
      load()
    } catch (err) {
      setMsg(`Razorpay error: ${err.message}`)
    } finally {
      setPaying(null)
    }
  }

  async function saveEdit(e) {
    e.preventDefault()
    if (!editing) return
    setSaving(true)
    setMsg('')
    try {
      const r = await fetch(`${API_BASE}/affiliate/admin/payouts/${editing.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: editing.status, amount: editing.amount, note: editing.note }),
      })
      const d = await r.json()
      if (!r.ok) throw new Error(d.message)
      setMsg(`Payout #${editing.id} updated.`)
      setEditing(null)
      load()
    } catch (err) {
      setMsg(err.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="title-font text-xl font-bold text-gray-900">Referral Payouts</h2>
        <div className="flex gap-2">
          {['PENDING', 'PAID', ''].map(s => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold transition ${
                filter === s ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white' : 'border border-orange-300 text-orange-700 hover:bg-orange-50'
              }`}
            >
              {s || 'All'}
            </button>
          ))}
          <button onClick={load} className="rounded-full border border-gray-300 px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-50">↻</button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : payouts.length === 0 ? (
        <p className="text-sm text-gray-400">No {filter.toLowerCase() || ''} payouts found.</p>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-gray-400">{total} record(s) · Total pending: ₹{payouts.filter(p => p.status === 'PENDING').reduce((s, p) => s + (p.amount || 0), 0)}</p>
          {msg && (
            <p className={`text-sm font-medium px-1 ${msg.startsWith('✓') || msg.includes('updated') ? 'text-green-600' : 'text-red-500'}`}>{msg}</p>
          )}
          {payouts.map(p => (
            <div key={p.id} className="rounded-2xl border border-orange-100 bg-white p-4 shadow-sm space-y-2">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-bold text-gray-900">{p.referrerName} <span className="text-gray-400 font-normal text-xs">({p.referrerEmail})</span></p>
                  <p className="text-xs text-gray-500">Payout #{p.id} · Referred user #{p.referredId} · {new Date(p.createdAt).toLocaleDateString()}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`rounded-full px-3 py-1 text-xs font-bold ${
                    p.status === 'PAID' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>{p.status}</span>
                  {p.amount > 0 && <span className="text-sm font-bold text-orange-600">₹{p.amount}</span>}
                  {p.status === 'PENDING' && (
                    <button
                      disabled={paying === p.id}
                      onClick={() => initiateRazorpayPayout(p.id)}
                      className="rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 px-3 py-1 text-xs font-semibold text-white hover:from-blue-600 hover:to-blue-700 disabled:opacity-50"
                    >
                      {paying === p.id ? 'Paying…' : '💸 Pay via Razorpay'}
                    </button>
                  )}
                  <button
                    onClick={() => setEditing({ id: p.id, status: p.status, amount: p.amount || 0, note: p.note || '' })}
                    className="rounded-lg border border-orange-300 px-3 py-1 text-xs font-semibold text-orange-700 hover:bg-orange-50"
                  >
                    Edit
                  </button>
                </div>
              </div>

              {/* Bank details */}
              {p.bankDetail ? (
                <div className="rounded-xl bg-orange-50 px-4 py-3 text-xs space-y-0.5">
                  <p className="font-semibold text-gray-700 mb-1">Bank Details</p>
                  <p><span className="text-gray-500">Name:</span> {p.bankDetail.accountName}</p>
                  <p><span className="text-gray-500">Bank:</span> {p.bankDetail.bankName}</p>
                  <p><span className="text-gray-500">Account:</span> {p.bankDetail.accountNumber}</p>
                  <p><span className="text-gray-500">IFSC:</span> {p.bankDetail.ifscCode}</p>
                  {p.bankDetail.upiId && <p><span className="text-gray-500">UPI:</span> {p.bankDetail.upiId}</p>}
                </div>
              ) : (
                <p className="text-xs text-red-400 italic font-medium">⚠ No bank details on file — cannot pay.</p>
              )}

              {p.note && <p className="text-xs text-gray-500 italic">Note: {p.note}</p>}

              {/* Inline edit form */}
              {editing?.id === p.id && (
                <form onSubmit={saveEdit} className="rounded-xl border border-orange-200 bg-orange-50 p-4 space-y-3 mt-2">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Status</label>
                      <select
                        value={editing.status}
                        onChange={e => setEditing(v => ({ ...v, status: e.target.value }))}
                        className="w-full rounded-xl border border-orange-200 bg-white px-3 py-2 text-sm"
                      >
                        <option value="PENDING">PENDING</option>
                        <option value="PAID">PAID</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Amount (₹)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={editing.amount}
                        onChange={e => setEditing(v => ({ ...v, amount: e.target.value }))}
                        className="w-full rounded-xl border border-orange-200 bg-white px-3 py-2 text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Note (optional)</label>
                    <input
                      type="text"
                      value={editing.note}
                      onChange={e => setEditing(v => ({ ...v, note: e.target.value }))}
                      placeholder="Payment reference, UTR, etc."
                      className="w-full rounded-xl border border-orange-200 bg-white px-3 py-2 text-sm"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button type="submit" disabled={saving}
                      className="rounded-full bg-gradient-to-r from-red-500 to-orange-500 px-4 py-1.5 text-xs font-semibold text-white disabled:opacity-60">
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                    <button type="button" onClick={() => setEditing(null)}
                      className="rounded-full border border-gray-300 px-4 py-1.5 text-xs text-gray-600 hover:bg-gray-50">
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
